import type { ReactNode } from "react";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
  WalletOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../../hooks/useViewport";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";
import { depositTitleOf, type DepositCoverage } from "../deposits/billDeposit";
import { CLOSE_COLUMN_LABEL } from "./closeLayout";
import type { CloseDepositChoice } from "./useCloseDeposit";

interface DepositPaymentSummaryProps {
  deposit: CloseDepositChoice;
  coverage: DepositCoverage;
  /** Сметката (поръчаното) като низ с два знака. */
  totalAmount: string;
  /** Името на метода за остатъка; null — не е избран или не трябва. */
  remainderMethodName: string | null;
  /** Повторен опит — депозитът и методът са записаните. */
  recorded: boolean;
  /** Сметка 0.00 със закачен депозит — BE отказва закриването. */
  blocksZeroBill: boolean;
  isNarrow: boolean;
}

/**
 * „Закрий" със закачен депозит — сметката като на касова лента, за да е ясно
 * какво се приспада и колко остава:
 *   Сметка                      10.40
 *   👛 Депозит № 8 · име       −10.40   (от 30.00 EUR)
 *   За доплащане                 0.00 EUR
 * и под нея „При закриване" — какво точно ще стане: дали гостът доплаща и с
 * какво, че депозитът се изразходва и колко от него изгаря (не се връща).
 * Покрие ли депозитът всичко, модалът е само това (без методите).
 */
export function DepositPaymentSummary({
  deposit,
  coverage,
  totalAmount,
  remainderMethodName,
  recorded,
  blocksZeroBill,
  isNarrow,
}: DepositPaymentSummaryProps) {
  const { t } = useTranslation();
  const { isTablet } = useViewport();
  const coversAll = coverage.remainderCents === 0;
  const billCents = parseMoneyToCents(totalAmount) ?? 0;
  const depositCents = parseMoneyToCents(deposit.amount) ?? 0;
  // Моноширинни са само цифрите — „EUR" и надписите са с текстовия шрифт,
  // като в останалата част на „Закрий" (`AmountFigure`).
  const money = "font-pos-mono font-semibold [font-variant-numeric:tabular-nums]";
  const currency = "text-[12px] font-semibold text-pos-muted";
  const rowText = isNarrow ? "text-[14px]" : "text-[15px]";

  const outcomes: { tone: OutcomeTone; text: ReactNode }[] = blocksZeroBill
    ? [{ tone: "danger", text: t("closeTable.deposit.zeroBill") }]
    : [
        ...(recorded ? [{ tone: "info" as const, text: t("closeTable.deposit.outcomeRetry") }] : []),
        coversAll
          ? { tone: "done" as const, text: t("closeTable.deposit.outcomeCovers") }
          : {
              tone: "done" as const,
              text: t("closeTable.deposit.outcomePartial", {
                amount: formatCents(coverage.appliedCents),
              }),
            },
        coversAll
          ? coverage.burnedCents > 0
            ? {
                tone: "danger" as const,
                text: t("closeTable.deposit.burned", { amount: formatCents(coverage.burnedCents) }),
              }
            : { tone: "done" as const, text: t("closeTable.deposit.outcomeUsedUp") }
          : remainderMethodName
            ? {
                tone: "done" as const,
                text: t("closeTable.deposit.outcomeRemainder", {
                  amount: formatCents(coverage.remainderCents),
                  method: remainderMethodName,
                }),
              }
            : { tone: "pending" as const, text: t("closeTable.deposit.pickRemainderMethod") },
      ];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Сметката: сметка − депозит = за доплащане */}
      <div className="rounded-xl border border-pos-line2 bg-pos-card">
        <div className={`flex items-baseline justify-between gap-3 px-4 pt-3.5 ${rowText}`}>
          <span className="font-semibold text-pos-soft">{t("closeTable.deposit.billAmount")}</span>
          <span className="shrink-0">
            <span className={`${money} text-pos-ink`}>{formatCents(billCents)}</span>{" "}
            <span className={currency}>EUR</span>
          </span>
        </div>
        <div className={`flex items-start justify-between gap-3 px-4 pb-3.5 pt-2.5 ${rowText}`}>
          <span className="flex min-w-0 items-start gap-2">
            {/* `!` — `.anticon { color: inherit }` на antd е извън Tailwind layer-ите. */}
            <WalletOutlined className="mt-[3px] shrink-0 text-[14px] !text-pos-accent" />
            <span className="min-w-0">
              <span className="block break-words font-semibold leading-snug text-pos-accent-ink">
                {depositTitleOf(t, deposit)}
              </span>
              {depositCents !== coverage.appliedCents && (
                <span className="block text-[12px] font-medium text-pos-muted">
                  {t("closeTable.deposit.ofTotal", { amount: deposit.amount })}
                </span>
              )}
            </span>
          </span>
          <span className="shrink-0">
            <span className={`${money} text-pos-accent-ink`}>−{formatCents(coverage.appliedCents)}</span>{" "}
            <span className={currency}>EUR</span>
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-dashed border-pos-line px-4 py-3">
          <span className={`font-extrabold text-pos-ink ${isNarrow ? "text-[15px]" : "text-base"}`}>
            {t("closeTable.deposit.due")}
          </span>
          <span className="flex shrink-0 items-baseline gap-1.5">
            <span
              className={`font-pos-mono font-bold leading-none tracking-[-.02em] [font-variant-numeric:tabular-nums] ${
                isNarrow || isTablet ? "text-[28px]" : "text-[34px]"
              } ${
                blocksZeroBill ? "text-pos-ink" : coversAll ? "text-pos-served" : "text-pos-accent"
              }`}
            >
              {formatCents(coverage.remainderCents)}
            </span>
            <span className="text-[13px] font-bold text-pos-muted">EUR</span>
          </span>
        </div>
      </div>

      {/* Какво ще стане при „Закриване" — етикетът е като „Изберете метод:" */}
      <div>
        <div className={CLOSE_COLUMN_LABEL}>{t("closeTable.deposit.outcomeTitle")}</div>
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {outcomes.map((outcome, index) => (
            <OutcomeItem key={index} tone={outcome.tone} isNarrow={isNarrow}>
              {outcome.text}
            </OutcomeItem>
          ))}
        </ul>
      </div>
    </div>
  );
}

type OutcomeTone = "done" | "pending" | "danger" | "info";

const OUTCOME_TONES: Record<OutcomeTone, { icon: ReactNode; text: string }> = {
  done: { icon: <CheckCircleFilled className="!text-pos-served" />, text: "text-pos-ink2" },
  // Празен кръг — стъпката чака избор.
  pending: {
    icon: <span className="m-px size-[13px] rounded-full border-2 border-pos-check" />,
    text: "text-pos-soft2",
  },
  danger: {
    icon: <ExclamationCircleFilled className="!text-pos-danger-ink" />,
    text: "font-semibold text-pos-danger-ink",
  },
  info: { icon: <InfoCircleFilled className="!text-pos-issued" />, text: "text-pos-ink2" },
};

/** Един ред от „При закриване" — иконка по тона и текстът (пренася се). */
function OutcomeItem({
  tone,
  isNarrow,
  children,
}: {
  tone: OutcomeTone;
  isNarrow: boolean;
  children: ReactNode;
}) {
  const { icon, text } = OUTCOME_TONES[tone];
  return (
    <li
      className={`flex items-start gap-2.5 font-medium leading-snug ${
        isNarrow ? "text-[13.5px]" : "text-[14px]"
      } ${text}`}
    >
      <span className="mt-0.5 inline-flex shrink-0 text-[15px]">{icon}</span>
      <span className="min-w-0 break-words">{children}</span>
    </li>
  );
}
